import {Stack, Space, Title, Text, Button, rem, Box} from '@mantine/core';
import {Overview} from "./Overview";
import {PageTitleText} from "./PageTitleText";
import {NormalText} from "./NormalText";
import {CommandNameText} from "./CommandNameText";
import {CodeBox} from "./CodeBox";
import {IconChevronDown} from "@tabler/icons-react";
import {Link} from "react-router-dom";

const installationText = {
    command: "pip install pylagg",
    subCommand: "",
    input: ""
}

const usageText = {
    command: "lagg",
    subCommand: "cgr",
    input: "<accession> -k <kmer size>"
}

const uninstallationText = {
    command: "pip uninstall pylagg",
    subCommand: "",
    input: ""
}

export function GettingStartedTextSection() {
    const link = "#overview"
    const scrollToSection = (link: string) => {
        const element = document.querySelector(link);
        if (element) {
            element.scrollIntoView({behavior: 'smooth'});
        }
    };

    return (
        <Stack
            gap="xl"
            mr="xl"
            ml="xl"
            mb="xl"
            maw="60rem"
        >
            <Box pos="relative">

                <svg
                    height="100%"
                    width="calc(100% + 200px)"
                    viewBox="0 0 1300 1024"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                        position: 'absolute',
                        top: 50,
                        left: -100,
                        opacity: 0.3,
                    }}>
                    <filter id="displacementFilter">
                        <feTurbulence
                            type="fractalNoise"
                            baseFrequency="0.03"
                            numOctaves="3"
                            result="noise"/>
                        <feDisplacementMap
                            in="SourceGraphic"
                            in2="noise"
                            scale="50"
                            xChannelSelector="R"
                            yChannelSelector="G"/>
                    </filter>
                    <mask id="circleMask">
                        <ellipse cx="650" cy="512" rx="612" ry="512" fill="white"
                                 style={{filter: "url(#displacementFilter"}}/>
                    </mask>
                    <image
                        href="/cgrdemograph.png"
                        width="100%"
                        height="100%"
                        mask="url(#circleMask)"
                        preserveAspectRatio={"xMidYMid slice"}
                        style={{filter: "grayscale(100%)"}}/>
                </svg>

                <Stack
                    gap="xl"
                    justify="center"
                    align="center"
                    pos="relative"
                    style={{
                        minHeight: "calc(100vh - 272px)"
                    }}
                >

                    <Stack gap="xs">
                        <Title size="5rem" ta="center">LAGG: Looking At Genomes Graphically</Title>
                        <Text ta="center">
                            <NormalText>
                                Looking at Genomes Graphically (LAGG) is a CLI tool for creating images from DNA
                                sequences.
                                <Space h="s"/>
                                You can try out our chaos game representation generation algorithm in our demo section.
                            </NormalText>
                        </Text>
                    </Stack>
                    <Space h="xs"/>
                    <Stack w="100%">
                        <NormalText>
                            LAGG is capable of generating an image providing just an SRA (or ENA) accession number and a
                            k-mer
                            count.
                            Of course, the CLI contains more options and even a config-based workflow for more complex
                            processes.
                        </NormalText>

                        <NormalText>
                            Images are generated using an algorithm based on Chaos Game Representation (CGR).
                            This process creates images by counting k-mers for a genome / DNA sequence,
                            with genomes acquired from the European Nucleotide Archive (ENA).
                        </NormalText>
                    </Stack>
                    <Button radius="xl" color="#2A83A1"
                            onClick={(event) => {
                                event.preventDefault();
                                scrollToSection(link);
                            }}>
                        <IconChevronDown style={{width: rem(18), height: rem(18)}} stroke={1.5}/>
                    </Button>
                </Stack>
            </Box>
            <Space h="xl"/>
            <Space h="xl"/>
            <p id="overview"></p>
            <Space h="xl"/>
            <Stack gap={'m'}>
                <Overview/>
                <PageTitleText>
                    Getting Started
                </PageTitleText>
            </Stack>
            <Stack gap={'m'} id="requirements">
                <CommandNameText>
                    Requirements
                </CommandNameText>

                <NormalText>
                    LAGG makes use of Jellyfish as a required dependency for k-mer counting.
                    On Debian/Ubuntu, Jellyfish is available on APT as "jellyfish". On MacOS, Jellyfish is available on
                    Homebrew also as "jellyfish".
                    Further instructions can be found on the GitHub page for Jellyfish:
                    <Link
                        to={"https://github.com/gmarcais/Jellyfish"}
                        style={{
                            textDecoration: "none",
                            color: "light-dark(#081317, #CFD4D6)"
                        }}
                    > https://github.com/gmarcais/Jellyfish
                    </Link>.
                </NormalText>

                <NormalText>
                    To install, LAGG requires Python version 3.10 or above. Any installation of Python also comes with
                    the "pip" package installer.
                </NormalText>
            </Stack>

            <Stack gap={'m'} id="installation">
                <CommandNameText>
                    Installation
                </CommandNameText>

                <NormalText>
                    After installing dependencies, install LAGG using pip with the following command:
                </NormalText>
                <CodeBox codeText={installationText}/>
            </Stack>

            <Stack gap={'m'} id="usage">
                <CommandNameText>
                    Usage
                </CommandNameText>

                <NormalText>
                    Using LAGG is as simple as executing the lagg command.
                </NormalText>

                <NormalText>
                    For example, to generate an image from an SRA or ENA accession number:
                </NormalText>
                <CodeBox codeText={usageText}/>

                <NormalText>
                    Replace 'accession' with any accession number (try ERR4770013 for a small COVID-19 genome)

                    The 'kmer_size' is an integer used when counting kmers which can eventually determine the
                    size of
                    the image.
                    For larger genomes, consider a size of 9-10. For smaller ones, consider 5-8.
                </NormalText>
                <NormalText>
                    For more options or help type 'lagg --help or visit the command documentation on the
                    left-hand side.
                </NormalText>
            </Stack>

            <Stack gap={'m'} id="uninstallation">
                <CommandNameText>
                    Uninstallation
                </CommandNameText>

                <NormalText>
                    To uninstall LAGG, run the following command in the terminal:
                </NormalText>
                <CodeBox codeText={uninstallationText}/>

                <Space h='xs'/>
            </Stack>
        </Stack>
    )
        ;
}